import React, { memo, useEffect, useMemo } from "react";
import useTranslation from "next-translate/useTranslation";
import BusinessFieldsType from "types/business.types";
import Breadcrumbs from "components/Breadcrumbs/Breadcrumbs";

export const OTHER_SUBCATEGORY_ID = 47;

type BusinessProfileBreadcrumbProps = {
  profile: BusinessFieldsType;
};
type BreadcrumbItem = {
  label: string;
  href?: string;
};

function BusinessProfileBreadcrumb({
  profile,
}: BusinessProfileBreadcrumbProps) {
  const { t } = useTranslation("common");

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    if (!profile) return [];

    const location = profile.locations.items?.[0]?.address;
    const subcategory = profile.industry.businessSubcategories.filter(
      (item) => item.id !== OTHER_SUBCATEGORY_ID
    )[0];

    const home: BreadcrumbItem = {
      label: "Home",
      href: `${process.env.NEXT_PUBLIC_DOMAIN_URL}/l`,
    };
    const subCategory: BreadcrumbItem = {
      label: subcategory.listingName,
      href: `${home.href}/${subcategory.listingSlugName}`,
    };

    const city: BreadcrumbItem | undefined = location?.city
      ? {
          label: `${location.country} - ${location.city}`,
          href: `${subCategory.href}/${location.formattedCountry}-${location.formattedCity}`,
        }
      : undefined;

    const neighborhood: BreadcrumbItem | undefined =
      location?.neighborhood && city
        ? {
            label: location.neighborhood,
            href: `${city.href}/${location.formattedNeighborhood}`,
          }
        : undefined;

    const business: BreadcrumbItem = {
      label: profile.name,
    };

    return [home, subCategory, city, neighborhood, business].filter(
      Boolean
    ) as BreadcrumbItem[];
  }, [profile]);

  return (
    <div className="mt-3">
      <Breadcrumbs>
        {breadcrumbItems.map((item, index) => {
          return item.href ? (
            <a key={index} href={item.href} rel="noopener noreferrer">
              {item.label}
            </a>
          ) : (
            <span key={index}>{item.label}</span>
          );
        })}
      </Breadcrumbs>
    </div>
  );
}

export default memo(BusinessProfileBreadcrumb);
